import { forwardRef, useEffect, useMemo, useRef } from "react";
import { useFormContext, Controller } from "react-hook-form";
import { useParams } from "react-router-dom";
import { MDBRow, MDBCol } from "mdbreact";

import "./FeuE3RiskAssessment.scss";

import { Button, Label, TextArea } from "abs-ui-components";

import { FeuECommonColumns } from "../../FeuConstants";
import { callApi } from "../../../../../services/MonitoringPlanService";
import { emitToasterMsg } from "../../../../../services/toasterUtility";
import { API_ROUTES } from "../../FeuApiRoutes/FeuApiRoutes";
import AutoSave from "../../../../../common/AutoSave/AutoSave";
import { AUTOSAVE_SUCCESS_MESSAGE } from "../../../Mrv/Constants";

const FeuE3RiskAssessment = forwardRef(({ FeuE3RiskAssessmentData, refetchMonitoringData,vesselMstId }, ref) => {
   
    const { control, getValues, reset, formState } = useFormContext();
    const { isDirty } = formState;
    const lastManualSave = useRef(null);
    const manualSaveTriggered = useRef(false);

    useEffect(() => {
        if (FeuE3RiskAssessmentData) {
            reset(prev => ({ ...prev, e3: FeuE3RiskAssessmentData }));
        }
    }, [FeuE3RiskAssessmentData, reset]);

    const e3Columns = useMemo(() => {
        return FeuECommonColumns
            .filter(col => col.name !== "versionOfProcedure")
            .map(col =>
                col.name === "referenceToProcedure"
                    ? {
                        ...col,
                        label: "Reference for procedure",
                        name: "referenceToProcedure",
                        maxLength: 1000,
                    }
                    : col
            );
    }, []);

    AutoSave(ref, [], async () => {
        if (!isDirty) return;
        await handleSave(true);
    });

    const handleSave = async (isAuto = false) => {
        const formValues = getValues("e3");
        const payload = {
            ...e3Columns.reduce((acc, { name }) => {
                acc[name] = formValues?.[name] || "";
                return acc;
            }, {}),
        };
        try {
            const FeuE3RiskAssessment = await callApi({
                method: "POST",
                url: API_ROUTES.E3(vesselMstId),
                data: payload,
            });
            if (FeuE3RiskAssessment?.isSuccess) {
                emitToasterMsg("success", "Table E.3", isAuto ? AUTOSAVE_SUCCESS_MESSAGE : "Created successfully");
                reset(prev => ({ ...prev, e3: formValues }));
                if (!isAuto) {
                    lastManualSave.current = Date.now();
                }
                if (typeof refetchMonitoringData === "function") {
                    await refetchMonitoringData();
                }
            }
            else {
                emitToasterMsg("error", "Table E.3", FeuE3RiskAssessment?.errorMessage);
            }
        } catch (error) {
            emitToasterMsg("error", "Table E.3", error?.errorMessage);
        }
    };

    return (
        <div className="e3-section-form mt-4">
            <MDBRow className="mb-2">
                <MDBCol md="4">
                    <h6 className="titles-font">Title of procedure</h6>
                </MDBCol>
                <MDBCol md="4">
                    <h6 className="titles-font">
                        Procedures for risk assessment
                    </h6>
                </MDBCol>
            </MDBRow>

            {e3Columns.map(({ label, name, maxLength, type }, index) => (
                <MDBRow className="align-items-center mb-3" key={index}>
                    <MDBCol md="4">
                        <Label name={label} bold />
                    </MDBCol>
                    <MDBCol md="4">
                        <Controller
                            name={`e3.${name}`}
                            control={control}
                            render={({ field }) => (
                                <TextArea
                                    id={`e3_${name}`}
                                    className="textarea-resize"
                                    value={field.value || ""}
                                    onChange={field.onChange}
                                    placeholder=""
                                    maxLength={maxLength}
                                    rows={1}
                                />
                            )}
                        />
                    </MDBCol>
                </MDBRow>
            ))}

            <div className="text-center">
                <Button id="feue3_save_btn"
                    disabled={!isDirty}
                    onClick={async () => {
                        manualSaveTriggered.current = true;
                        await handleSave(false);
                    }}
                >
                    Save
                </Button>
            </div>
        </div>
    );
});

export default FeuE3RiskAssessment;
